import { t } from './sprache'
import type { Sprachschluessel } from './sprache'

/**
 * Der Spaltenaufriss eines Berichts.
 *
 * **Dieselben fünf Bilder in Bilanz, GuV und Kapitalflussrechnung** (R167).
 * Ein Anwender, der gelernt hat, wie er in der Bilanz auf Perioden umschaltet,
 * soll es in der GuV nicht wieder lernen müssen. Deshalb steht die Wahl nicht
 * in den Bauteilen, sondern hier, und die Bildschirme reichen sie nur durch.
 *
 * **Jedes Bild trägt seinen vollen Namen** — berichtigt am 2026-09-13 auf den
 * Befund des Auftraggebers: *„Ich bin mit den Symbolen des Strukturaufrisses
 * noch nicht glücklich. Schreibe die Buttons wörtlich aus. Derzeit haben wir
 * ja genug Platz dafür."*
 *
 * Vorher standen hier fünf Strichzeichnungen, und nur das gewählte Bild trug
 * sein Kurzwort daneben (R283). Zweimal nachgezeichnet, zweimal nicht
 * getroffen: Diese fünf Bilder sind Sachverhalte des Konzernabschlusses, keine
 * Gegenstände — ein *Konsolidierungssachverhalt* hat kein Bild, das man
 * zeichnen könnte, ohne es zu erklären. Ein Symbol, das seinen Tooltip
 * braucht, ist der Name in schlecht.
 *
 * Das Symbol fällt damit ganz weg und steht nicht vor dem Namen: Neben seinem
 * eigenen ausgeschriebenen Wort beantwortet es dieselbe Frage zweimal, und
 * das ist die Stelle, an der Platz verlorengeht statt gewonnen zu werden.
 *
 * **Das Raster steht nicht hier.** Seit dem 2026-09-10 gehört es zur Zeit und
 * damit in die Kontextzeile, neben den Zeitschieber (7.3 Abschnitt 2g): Es
 * sagt, in welchen Zeiteinheiten die Zeit gezählt wird — für jedes Bild, nicht
 * nur für das Periodenbild.
 */
export type Spaltenmodus = 'konsolidierung' | 'kurz' | 'struktur' | 'partner' | 'perioden'

/**
 * Die fünf Bilder, in der Reihenfolge der Leiste: **Perioden, Konzernstruktur,
 * Bewertungsschichten, Partner** (7.3 Abschnitt 24, R1013 — Auftrag vom
 * 2026-10-04). Die Kurzform steht neben dem Bild, das sie verkürzt; heißt das
 * Grundbild *Konsolidierungssachverhalt*, steht es an der Stelle der
 * Bewertungsschichten.
 *
 * Bis zum 2026-10-04 lief die Leiste vom vollen Weg zur Konzernzahl über die
 * Schnitte nach Gesellschaft und Partner zum Zeitverlauf.
 */
const BILDER: { wert: Spaltenmodus; name: Sprachschluessel }[] = [
  { wert: 'perioden', name: 'spalten.perioden' },
  { wert: 'struktur', name: 'spalten.struktur' },
  { wert: 'konsolidierung', name: 'spalten.konsolidierung' },
  { wert: 'kurz', name: 'spalten.kurz' },
  { wert: 'partner', name: 'spalten.partner' },
]

export function Spaltenwahl({
  modus,
  aufModus,
  nur,
  geliefert,
}: {
  modus: Spaltenmodus
  aufModus: (m: Spaltenmodus) => void
  /**
   * Welche Bilder überhaupt zur Wahl stehen — `undefined` heißt: alle fünf.
   *
   * **Ein Bericht auf eine einzelne Gesellschaft hat nur eines** (R714): Die
   * übrigen vier zeigten den Konzern, obwohl oben eine Gesellschaft gewählt
   * ist. Bleibt nur eines übrig, fällt die Leiste ganz weg — eine Wahl mit
   * einem Eintrag ist keine (R293).
   */
  nur?: Spaltenmodus[]
  /**
   * Ob die gelieferte Sicht gilt (R759) — dann heißt das erste Bild nach dem,
   * was darin steht.
   *
   * **Ohne Lauf gibt es keinen Konsolidierungssachverhalt.** Was das Bild
   * dort zeigt, sind die **Bewertungsschichten**: die gelieferten Rohdaten
   * und die Anpassung darüber. Ein Name, der etwas anderes verspricht, ist
   * schlechter als keiner.
   */
  geliefert?: boolean
}) {
  const bilder = (nur ? BILDER.filter((b) => nur.includes(b.wert)) : BILDER).map((b) =>
    geliefert && b.wert === 'konsolidierung' ? { ...b, name: 'spalten.schichten' as const } : b,
  )

  if (bilder.length < 2) {
    return null
  }

  return (
    <div className="spaltenwahl">
      <span className="beschriftung">{t('spalten.titel')}</span>

      <div className="knopfreihe" role="radiogroup" aria-label={t('spalten.titel')}>
        {bilder.map((b) => (
          <button
            key={b.wert}
            type="button"
            role="radio"
            aria-checked={modus === b.wert}
            className={modus === b.wert ? 'aktiv' : ''}
            data-name={t(b.name)}
            onClick={() => aufModus(b.wert)}
          >
            {t(b.name)}
          </button>
        ))}
      </div>
    </div>
  )
}
